import { useEffect, useState, useMemo } from "react";
import { ChevronDown, Search } from "lucide-react";
import { Button } from "../ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { myLocalStorage } from "../StorageHelper";
import { AWSAccount, AzureSubscription } from "./Cloud/interface";
import {
    awsAccountList,
    awsTenantAccountList,
    azureSubcriptionList,
    gcpProjectList,
} from "./Cloud/Data";
import useUserStore from "../../services/userStore";

interface CloudResourceSelectorProps {
    cloudProvider: string;
    onSelectorChange?: (value: string) => void;
    isLabelNeeded?: boolean;
    isAdmin?: boolean;
}

export default function CloudAccountList({
    cloudProvider,
    onSelectorChange,
    isLabelNeeded = true,
    isAdmin = false,
}: CloudResourceSelectorProps) {
    const { email } = useUserStore((state) => state.user || {});
    const [selectedOption, setSelectedOption] = useState<string>("");
    const [items, setItems] = useState<string[] | AWSAccount[] | AzureSubscription[]>([]);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(false);


    const fetchResources = async () => {
        setLoading(true);
        try {
            let data: string[] | AWSAccount[] | AzureSubscription[] = [];

            if (cloudProvider.toLowerCase() === "gcp") {
                data = await gcpProjectList();
            } else if (cloudProvider.toLowerCase() === "aws") {
                if (isAdmin) {
                    data = await awsAccountList(email);

                } else {
                    data = await awsTenantAccountList(email);
                }

            } else if (cloudProvider.toLowerCase() === "azure") {
                data = await azureSubcriptionList();
            }

            setItems(data);

            let storedValue: string | null = null;
            if (cloudProvider === "gcp") {
                storedValue = myLocalStorage.getItem("gcpSelectedProjectId");
            } else if (cloudProvider === "aws") {
                storedValue = myLocalStorage.getItem("selectedAwsAccount");
            } else if (cloudProvider === "azure") {
                const azureStored = myLocalStorage.getItem("latestSubList");
                if (azureStored) {
                    try {
                        const azureObj = JSON.parse(azureStored);
                        storedValue = azureObj?.azureSubscriptionId ?? null;
                    } catch {
                        storedValue = null;
                    }
                }
            }

            let selectedItem: string | AWSAccount | AzureSubscription | null = null;
            if (storedValue) {
                selectedItem = (data as any[]).find(
                    (item) => getUniqueId(item, cloudProvider) === storedValue
                );
            }

            if (!selectedItem && data.length > 0) {
                selectedItem = data[0];
            }

            if (selectedItem) {
                const displayName = getDisplayName(selectedItem, cloudProvider);
                const uniqueId = getUniqueId(selectedItem, cloudProvider);

                setSelectedOption(displayName);
                onSelectorChange?.(uniqueId);

                if (cloudProvider.toLowerCase() === "gcp") {
                    myLocalStorage.setItem("gcpSelectedProjectId", uniqueId);
                } else if (cloudProvider.toLowerCase() === "aws") {
                    myLocalStorage.setItem("selectedAwsAccount", uniqueId);
                } else if (cloudProvider.toLowerCase() === "azure") {
                    myLocalStorage.setItem(
                        "latestSubList",
                        JSON.stringify({
                            azureSubscriptionId: uniqueId,
                            subscriptionName: displayName,
                        })
                    );
                }
            }
        } catch (err) {
            console.error(`Error loading ${cloudProvider} resources:`, err);
            setItems([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchResources();
    }, [cloudProvider, email, isAdmin]);

    const handleChange = (item: string | AWSAccount | AzureSubscription) => {
        const displayName = getDisplayName(item, cloudProvider);
        const uniqueId = getUniqueId(item, cloudProvider);
        setSelectedOption(displayName);
        onSelectorChange?.(uniqueId);

        if (cloudProvider.toLowerCase() === "gcp") {
            myLocalStorage.setItem("gcpSelectedProjectId", uniqueId);
        } else if (cloudProvider.toLowerCase() === "aws") {
            myLocalStorage.setItem("selectedAwsAccount", uniqueId);
        } else if (cloudProvider.toLowerCase() === "azure") {
            myLocalStorage.setItem(
                "latestSubList",
                JSON.stringify({
                    azureSubscriptionId: uniqueId,
                    subscriptionName: displayName,
                })
            );
        }
    };

    const filteredItems = useMemo(() => {
        const term = search.toLowerCase();
        const list = Array.isArray(items) ? items : [];

        return list.filter((item: any) =>
            getDisplayName(item, cloudProvider)?.toLowerCase().includes(term)
        );
    }, [items, search, cloudProvider]);

    if (loading) return <div>Loading...</div>;
    if (items.length === 0) return <div>No resources found</div>;

    return (
        <div className="flex items-start gap-4 flex-wrap">
            <div className="relative">
                {isLabelNeeded && (
                    <label
                        htmlFor="cloud-selector"
                        className="absolute -top-4 left-3 z-10 px-1 text-xs font-medium text-muted-foreground"
                    >
                        {cloudProvider.toLowerCase() === "gcp"
                            ? "Project List"
                            : cloudProvider.toLowerCase() === "azure"
                                ? "Subscription List"
                                : "Account List"}
                    </label>
                )}

                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button
                            id="cloud-selector"
                            variant="outline"
                            className="min-w-[240px] justify-between rounded-md"
                        >
                            {selectedOption || `Select ${getLabel(cloudProvider)}`}
                            <ChevronDown className="w-4 h-4 ml-2" />
                        </Button>
                    </DropdownMenuTrigger>

                    <DropdownMenuContent className="min-w-[240px] max-h-[300px] overflow-y-auto">
                        {/* 🔍 Search Box */}
                        <div className="flex items-center px-2 py-1.5 sticky top-0 bg-background border-b">
                            <Search className="w-4 h-4 text-muted-foreground mr-2" />
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search..."
                                className="w-full text-sm bg-transparent outline-none"
                            />
                        </div>

                        {filteredItems.length === 0 ? (
                            <div className="p-2 text-sm text-muted-foreground">No results found</div>
                        ) : (
                            filteredItems.map((item, idx) => (
                                <DropdownMenuItem
                                    key={getUniqueId(item, cloudProvider) || idx}
                                    onClick={() => handleChange(item)}
                                >
                                    {getDisplayName(item, cloudProvider)}
                                </DropdownMenuItem>
                            ))
                        )}
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </div>
    );
}

// 🔧 Helper functions remain unchanged
function getLabel(provider: string): string {
    switch (provider.toLowerCase()) {
        case "gcp":
            return "Project";
        case "aws":
            return "Account";
        case "azure":
            return "Subscription";
        default:
            return "Resource";
    }
}

function getDisplayName(item: string | AWSAccount | AzureSubscription, provider: string): string {
    if (provider === "gcp") return typeof item === "string" ? item : "";
    if (provider === "aws") {
        const aws = item as AWSAccount;
        return aws.accountName || aws.awsAccountName || aws.accountId || aws.awsAccountId || "";
    }
    if (provider === "azure") {
        const azure = item as AzureSubscription;
        return azure.subscriptionName || azure.azureSubscriptionId;
    }
    return "-";
}

function getUniqueId(item: string | AWSAccount | AzureSubscription, provider: string): string {
    if (provider === "gcp") return typeof item === "string" ? item : "";
    if (provider === "aws") {
        const aws = item as AWSAccount;
        return aws.accountId || aws.awsAccountId || "";
    }
    if (provider === "azure") return (item as AzureSubscription).azureSubscriptionId;
    return "";
}
